import {type WCFieldData} from './../../../@type/woocommerce/field-data';
import chevronDownImageURL from '../../img/chevron-down-solid.svg';

/**
 * All rendering additional fields functions.
 */

import {getLocaleText} from '../util/translation';
import {type WCFieldsData} from '../../../@type/woocommerce/field-data';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';
import {Environment, Feature, updateEnvironment} from '../reducers/environmentReducer';
import {PeachPayOrder, setExtraFields} from '../reducers/peachPayOrderReducer';
import {store} from '../store';
import {cleanFormFields} from '../util/form';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {type WCPHPData} from '../../../@type/woocommerce/php-data';
import {type InitMessage} from '../../../@type/window-messages';
import {FeatureFlag} from '../../../@type/features';

export function addOrderNotesToAdditionalFields(phpData: WCPHPData) {
	const fieldData: WCFieldsData[] = phpData?.additional_fields ?? ([] as WCFieldsData[]);
	const fieldOrder: number[] = phpData?.additional_fields_order?.map(fo => Number(fo)) ?? [];

	if (Feature.enabled(FeatureFlag.ORDER_NOTES_INPUT)) {
		const orderNotesData: WCFieldsData = {
			field_default: '',
			field_enable: 'yes',
			field_label: getLocaleText('Order notes'),
			field_name: 'order_comments',
			field_required: '',
			type_list: 'text',
			field_placeholder: ' ',
			width: '100',
		};
		// Example: if fieldOrder = [1, 3], make the array [1, 2, 3] and get the diff
		let temp = Array.from(Array(Object.keys(fieldData).length + 2).keys()).slice(1);
		temp = temp.filter(n => !fieldOrder.includes(n));
		const fieldId = temp[0];

		if (fieldId) {
			fieldData[fieldId] = orderNotesData;
			fieldOrder.push(fieldId);
		}
	}

	return {fieldData, fieldOrder};
}

export function initAdditionalFields(message: InitMessage) {
	const {fieldData, fieldOrder} = addOrderNotesToAdditionalFields(message.phpData);
	renderAdditionalFields(fieldData, fieldOrder);
}

function syncFields(event: Event) {
	const $form = (event.target as HTMLElement).closest('form');
	const fieldRecord: Record<string, string> = {};

	for (const $element of Array.from($form?.elements ?? []) as HTMLElement[]) {
		if ($element.tagName === 'INPUT') {
			const $input = $element as HTMLInputElement;
			if ($input.type === 'radio') {
				if ($input.checked) {
					fieldRecord[$input.name] = $input.value;
				} else {
					continue;
				}
			} else if ($input.type === 'checkbox') {
				if ($input.checked) {
					fieldRecord[$input.name] = 'checked';
				} else {
					fieldRecord[$input.name] = 'unchecked';
				}
			} else {
				fieldRecord[$input.name] = $input.value;
			}
		} else if ($element.tagName === 'SELECT') {
			const $select = $element as HTMLSelectElement;
			fieldRecord[$select.name] = $select.value;
		} else if ($element.tagName === 'TEXTAREA') {
			const $textarea = $element as HTMLTextAreaElement;
			fieldRecord[$textarea.name] = $textarea.value;
		}
	}

	// Collect only extra fields from form into fieldRecord
	cleanFormFields(fieldRecord);
	store.dispatch(setExtraFields(fieldRecord));
}

/* Updates the UI based on whether or not additional field is filled in / required */
function displayInputStatus(accordionHeader: HTMLElement, isInitial: boolean) {
	// Update the 'initial' classname for accordion-inner
	if (isInitial) {
		accordionHeader.classList.add('initial');
	} else {
		accordionHeader.classList.remove('initial');
	}
}

/* Allows the customer to navigate through / enter input into the radio/checkbox fields
   using keyboard events. */
function handleNonTextInputAccessibility(event: Event) {
	if (['ENTER', ' '].includes((event as KeyboardEvent).key)) {
		const $element = event.target as HTMLInputElement | HTMLTextAreaElement;
		const input = $element.querySelector('input');
		if (input) {
			if (input.type === 'radio') {
				input.checked = true;
			} else {
				input.checked = !input.checked;
			}
		}
	}

	syncFields(event);
}

/* Toggles the collapsed/expanded state of an accordion section, given its header element. */
function toggleAccordionCollapseState(accordionHeader: HTMLElement) {
	if (accordionHeader.classList.contains('fill')) {
		accordionHeader.classList.remove('fill');
	} else {
		accordionHeader.classList.add('fill');
	}

	const $inner = accordionHeader.nextElementSibling;
	if ($inner) {
		// Expand
		if ($inner.classList.contains('collapse')) {
			$inner.classList.remove('collapse');
			$inner.classList.remove('dim');
			$inner.querySelectorAll('input, textarea, select, .pp-radio-line, .pp-checkbox-input-container').forEach($element => {
				$element.setAttribute('tabindex', '0');
				if ($element.classList.contains('pp-radio-line') || $element.classList.contains('pp-checkbox-input-container')) {
					$element.addEventListener('keypress', handleNonTextInputAccessibility);
				}
			});
		} else { // Collapse
			$inner.classList.add('dim');
			setTimeout(() => {
				$inner.classList.add('collapse');
			}, 300);
			$inner.querySelectorAll('input, textarea, select, .pp-radio-line, .pp-checkbox-input-container').forEach($element => {
				$element.setAttribute('tabindex', '-1');
				if ($element.classList.contains('pp-radio-line') || $element.classList.contains('pp-checkbox-input-container')) {
					$element.removeEventListener('keypress', handleNonTextInputAccessibility);
				}
			});
		}
	}

	const $chevron = accordionHeader.getElementsByTagName('img')?.[0];
	if ($chevron) {
		if ($chevron.style.transform === 'scaleY(-1)') {
			$chevron.style.transform = 'scaleY(1)';
		} else {
			$chevron.style.transform = 'scaleY(-1)';
		}
	}
}

/* Determines if the input field is at an initial state. */
function inputIsInitial($inner: HTMLElement, $field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) {
	let isInitial = true;
	if ($field.tagName === 'INPUT') {
		if ($field.type === 'checkbox') {
			isInitial = !($field as HTMLInputElement).checked;
		} else if ($field.type === 'radio') {
			const $selectedOption: HTMLInputElement | null = $inner.querySelector('.extra-field:checked');
			isInitial = $selectedOption === null;
		} else {
			isInitial = $field.value === '';
		}
	} else if ($field.tagName === 'SELECT') {
		isInitial = $field.value === '' || $field.value.toLowerCase() === 'please select';
	} else {
		isInitial = $field.value === '';
	}

	return isInitial;
}

// KeyboardEvent listener for accessibility: accordion can be expanded/collapsed using keyboard events.
function accordionHeaderListener(event: KeyboardEvent) {
	const $ah = event.target as HTMLElement;
	if ($ah) {
		if (['ENTER', ' '].includes(event.key)) {
			event.stopImmediatePropagation();
			toggleAccordionCollapseState($ah);
		}
	}
}

/* Adds event handlers for focusing/opening/closing the additional fields. */
function addAdditionalFieldsEventHandlers() {
	$qsAll('.pp-accordion-header').forEach(($ah: HTMLElement) => {
		$ah.addEventListener('click', (event: Event) => {
			event.stopImmediatePropagation();
			toggleAccordionCollapseState($ah);
		});
	});

	// Adds event listeners to be able to press a key to open
	$qsAll('.pp-accordion-section').forEach(($as: HTMLElement) => {
		// Set the initial tabindex of the inner elements to -1 because we only want them to be
		// focusable when the accordion's expanded. Otherwise there is a UI bug
		$as.querySelectorAll('input, textarea, select, .pp-radio-line, .pp-checkbox-input-container').forEach($element => {
			$element.setAttribute('tabindex', '-1');
		});

		const $ah = $as.querySelector<HTMLElement>('.pp-accordion-header');
		if ($ah) {
			$ah.addEventListener('focus', () => {
				$ah?.addEventListener('keypress', accordionHeaderListener);
			});
			$ah.addEventListener('blur', () => {
				$ah?.removeEventListener('keypress', accordionHeaderListener);
			});
		}
	});
	$qsAll('.pp-new-field input, .pp-new-field textarea, .pp-new-field select').forEach($element => {
		$element.addEventListener('change', (event: Event) => {
			syncFields(event);
		});
	});
	$qs('#additional-fields-new')?.addEventListener('change', syncFields);
}

function renderAdditionalFields(fieldData: WCFieldsData[], fieldOrder: number[]) {
	if (fieldData.length === 0 || fieldOrder.length === 0) {
		return;
	}

	$qsAll('#additional-fields-new', $element => {
		$element.addEventListener('submit', (e: Event) => {
			e.preventDefault();
			return false;
		});
	});

	$qs('#additional-fields-new')?.classList.remove('hide');

	generateFields(fieldData, fieldOrder);

	addAdditionalFieldsEventHandlers();

	let previousExtraFields = PeachPayOrder.extraFieldsRecord();
	renderExtraFields(previousExtraFields);
	store.subscribe(() => {
		const extraFields = PeachPayOrder.extraFieldsRecord();
		if (JSON.stringify(previousExtraFields) !== JSON.stringify(extraFields)) {
			renderExtraFields(extraFields);
			previousExtraFields = extraFields;
		}
	});
}

/* Renders the additional fields with the given set of field data. */
function renderExtraFields(extraFieldData: Record<string, string>) {
	for (const [key, value] of Object.entries(extraFieldData)) {
		const $element = $qs<HTMLElement>(`[name="${key}"].extra-field`);
		if ($element?.tagName === 'INPUT') {
			const $input = $element as HTMLInputElement;
			if (['radio', 'checkbox'].includes($input.type)) {
				$qsAll<HTMLInputElement>(`[name="${key}"][value="${value}"].extra-field`, $radio => {
					$radio.checked = true;
				});
				continue;
			} else {
				$input.value = value;
			}
		} else if ($element?.tagName === 'SELECT') {
			const $select = $element as HTMLSelectElement;
			$select.value = value;
		} else if ($element?.tagName === 'TEXTAREA') {
			const $textarea = $element as HTMLTextAreaElement;
			$textarea.value = value;
		}
	}

	// Render the accordion header text so that it correctly indicates whether or not there is input.
	$qsAll('.pp-accordion-header').forEach(($ah: HTMLElement) => {
		const $inner = $ah.nextElementSibling as HTMLElement;
		const $field = $inner.querySelector<HTMLInputElement>('input, textarea, select');
		if ($field) {
			const isInitial = inputIsInitial($inner, $field);
			displayInputStatus($ah, isInitial);
		}
	});
}

function generateFields(fieldData: WCFieldsData[], fieldOrder: number[]) {
	const field = (fieldDataSingle: WCFieldsData, accordion: boolean) =>
		`<div class="pp-new-field">
			${generateFieldElement(fieldDataSingle, accordion)}
		</div>`;

	const newPageElement = document.querySelector('#additional-fields-new')!;

	if (fieldOrder.length === 1) {
		const f = fieldData?.[fieldOrder?.[0] ?? -1];
		if (f && newPageElement) {
			newPageElement.innerHTML += field(f, false);
		}
	} else if (newPageElement) {
		let fields = '';
		fieldOrder.forEach((orderNumber, index) => {
			const datum = fieldData[orderNumber];
			if (!datum) {
				return;
			}

			let fieldItem = field(datum, true);

			if (index === 0) {
				fieldItem = fieldItem.replace('pp-new-field', 'pp-new-field pp-new-field-first');
			}

			if (index + 1 === fieldOrder.length) {
				fieldItem = fieldItem.replace('pp-new-field', 'pp-new-field pp-new-field-last');
			}

			fields += fieldItem;
		});
		newPageElement.innerHTML += fields;
	}
}

function generateFieldElement(fieldData: WCFieldsData, accordion: boolean): string {
	let elementString = '';

	const accordionBuilder = (inner: string) => `
			<div class="pp-accordion-section">
				<div class="pp-accordion-header${fieldData.field_required ? ' required' : ''}" tabindex="0">
					<div class="pp-accordion-header-text">
						${fieldData.field_label + (fieldData.field_required ? '*' : '')}
					</div>
					<img src="${chevronDownImageURL}" class="pp-accordion-arrow"/>
					<div class="pp-accordion-header-bg"></div>
				</div>
				<div class="accordion-inner dim collapse" tabindex="-1">
					${inner}
				</div>
			</div>`;

	const formLabelBuilder = (labelText?: string) => {
		if (!labelText) {
			labelText = fieldData.field_label + (fieldData.field_required ? '*' : accordion ? '' : ' (optional)');
		}

		return `<label for="${fieldData.field_name}-new" class="pp-form-label-text">
			${labelText}
		</label>`;
	};

	const labelBuilder = (isAdditionalFieldOuterLabel?: boolean) => `<label ${isAdditionalFieldOuterLabel ? 'class="pp-additional-field-label" ' : ''} for="${fieldData.field_name}-new">
			${fieldData.field_label + (fieldData.field_required ? '*' : '')}
		</label>`;

	const textAreaBuilder = () => {
		const inner = `<textarea type="text"
			name="${fieldData.field_name}"
			class="text-input"
			id="${fieldData.field_name}-new"
			placeholder="${fieldData.field_placeholder ?? ' '}"
			${(fieldData.field_default ? `value="${fieldData.field_default}"` : '')}
			${(fieldData.field_required ? 'required' : '')}></textarea>`;

		if (!accordion) {
			return `<div class="flex-col">
				${fieldData.field_label ? labelBuilder(true) : ''}
				<div class="flex-col">
					${inner}
				</div>
			</div>`;
		}

		return accordionBuilder(`
			<div class="flex-col">
				${inner}
			</div>`);
	};

	const genericInputAreaBuilder = () => {
		const inner = `<input type="${fieldData.type_list}"
			name="${fieldData.field_name}"
			class="pp-input-box-text"
			id="${fieldData.field_name}-new"
			placeholder="${fieldData.field_placeholder ?? ' '}"
			${(fieldData.field_default ? `value="${fieldData.field_default}"` : '')}
			${(fieldData.field_required ? 'required' : '')}>`;

		if (!accordion) {
			return `<div class="flex-col">
				${inner}
				${fieldData.field_label ? formLabelBuilder() : ''}
			</div>`;
		}

		return accordionBuilder(`
				<div class="flex-col">
					${inner}
					${fieldData.field_label ? formLabelBuilder() : ''}
				</div>`);
	};

	const headerBuilder = () => {
		if (!accordion) {
			return `
				<div class="pp-accordion-section-header pp-accordion-header-text">
					${fieldData.field_label + (fieldData.field_required ? '*' : '')}
				</div>`;
		}

		return `
				<div class="pp-accordion-section">
					<div class="pp-accordion-section-header pp-accordion-header-text">
						${fieldData.field_label + (fieldData.field_required ? '*' : '')}
					</div>
					<div class="pp-accordion-section-header pp-accordion-header-bg"></div>
				</div>`;
	};

	const selectBuilder = (optionOrder: string[][]) => {
		const inner = `<div class="pp-select-container"><select name=${fieldData.field_name}
			id="${fieldData.field_name}-new"
			class="pp-${fieldData.type_list}-box new-text w-100 extra-field" ${fieldData.field_required ? 'required' : ''}
			>
			${optionBuilder(optionOrder) ?? ''}
		</select><div class="pp-select-chevron"><span></span></div></div>`;
		if (!accordion) {
			return `<div class="flex-col">
				${labelBuilder(true)}
				${inner}</div>`;
		}

		return accordionBuilder(inner);
	};

	const optionBuilder = (optionOrder: string[][]) => {
		if (optionOrder.length === 0) {
			return;
		}

		let optionList = '<option value="">Please Select</option>';
		optionOrder.forEach(value => {
			if (value[0] && value[1]) {
				optionList += `<option value="${value[0]}">${value[1]}</option>`;
			}
		});
		return optionList;
	};

	/**
	 * value[0] is converted to a string because while it is typed to be a string there is a
	 * situation where it is not. A string with an integer inside is converted to a number when used
	 * as a key in a JS object. The `option_order` property is set as an array in php which then
	 * gets converted to a JS object.
	 *
	 * Ex: setting radio button option values to integer strings gives the options object:
	 * {
	 *   1: "Option Text One",
	 *   2: "Option Text Two"
	 * }
	 */
	const radioFieldBuilder = (optionOrder: string[][]) => {
		let inner = '';
		optionOrder.forEach(value => {
			if (value[0] && value[1]) {
				inner += `
					<div class="pp-radio-line">
						<input type=${fieldData.type_list}
							name=${fieldData.field_name} 
							id="${fieldData.field_name + '-new-' + value[0]}"
							value="${value[0]}"
							class="input-${fieldData.type_list} extra-field"
							${(fieldData.field_required ? 'required' : '')}
							${fieldData.field_default === value[0].toString() ? 'checked="checked"' : ''}
						/>
						<label for="${fieldData.field_name}-new-${value[0]}">
							${value[1]}
						</label>
					</div>`;
			}
		});
		if (!accordion) {
			return `<div class="flex-col">
				${labelBuilder(true)}
				${inner}</div>`;
		}

		return accordionBuilder(inner);
	};

	const checkboxBuilder = () => {
		const inner = `
		<div class="pp-checkbox-input-container">
		<input
			type="checkbox"
			id="${fieldData.field_name}-new"
			class="pp-form-checkbox extra-field"
			name="${fieldData.field_name}"
			value="${fieldData.field_default ? fieldData.field_default : '1'}"
			${fieldData.field_required ? 'required' : ''}
		>
		${labelBuilder()}
		</div>`;
		if (!accordion) {
			return `<div class="flex-col">
				${labelBuilder(true)}
				${inner}</div>`;
		}

		if (!accordion) {
			return inner;
		}

		return accordionBuilder(inner);
	};

	if (fieldData.type_list === 'text') {
		elementString = textAreaBuilder();
		return elementString;
	}

	if (fieldData.type_list === 'select') {
		elementString += selectBuilder(fieldData.option_order ?? []);
		return elementString;
	}

	if (fieldData.type_list === 'radio') {
		elementString += radioFieldBuilder(fieldData.option_order ?? []);
		return elementString;
	}

	if (fieldData.type_list === 'checkbox') {
		elementString += checkboxBuilder();
		return elementString;
	}

	if (fieldData.type_list === 'header') {
		elementString += headerBuilder();
		return elementString;
	}

	elementString = genericInputAreaBuilder();
	return elementString;
}

export function collectAdditionalFieldData(fieldData: WCFieldsData[], fieldOrder: number[]): Record<string, string> {
	const fieldDataRecord: Record<string, string> = {};
	for (const orderNumber of fieldOrder) {
		const field = fieldData[orderNumber];
		if (!field) {
			continue;
		}

		const fieldName = field.field_name;
		const temporaryData: WCFieldData = {name: ''};
		temporaryData.label = field.field_label;
		temporaryData.name = fieldName;
		if (field.field_enable) {
			if (field.type_list === 'radio') {
				const $selectedRadio = $qs<HTMLInputElement>(`input[type="radio"][name=${fieldName}]:checked`);
				if ($selectedRadio?.value) {
					temporaryData.value = $selectedRadio?.value;
					fieldDataRecord[temporaryData.name] = temporaryData.value ?? '';
				}
			} else if (field.type_list === 'checkbox') {
				const $checkbox = $qs<HTMLInputElement>(`input[type="checkbox"][name=${fieldName}]`);
				temporaryData.value = $checkbox?.checked ? 'yes' : 'no';
				fieldDataRecord[temporaryData.name] = temporaryData.value ?? '';
			} else {
				const $element = $qs<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#${fieldName}-new`);
				temporaryData.value = $element?.value;
				fieldDataRecord[temporaryData.name] = temporaryData.value ?? '';
			}
		}
	}

	return fieldDataRecord;
}

/**
 * Checks the info page form and the additional fields.
 * If there are invalid additional fields, expands only the invalid fields before showing the help message.
 */
export function checkRequiredFields(): boolean {
	// Unless checkRequiredFields is called from the returning customer checkout
	// or the customer hasn't moved past the info page of the new customer checkout,
	// it would be redundant to check the info fields.
	const shouldCheckInfoForm = Environment.modalUI.page() === 'info' || Environment.modalUI.page() === 'shipping';

	if (shouldCheckInfoForm) {
		const modalPage = Environment.modalUI.page();
		const forms = modalPage === 'shipping' && PeachPayCustomer.shipToDifferentAddress() ? '#pp-different-shipping-details' : '#pp-info-form';
		const primaryFormValidity = $qsAll<HTMLFormElement>(`${forms}`).map($el => $el.checkValidity()).reduce((acc, validity) => acc === validity, true);
		if (!primaryFormValidity) {
			if (modalPage !== 'shipping') {
				store.dispatch(updateEnvironment({
					modalPageType: 'info',
					requestedPage: undefined,
				}));
			}

			$qs<HTMLFormElement>(`${forms}`)?.reportValidity();
			return false;
		}
	}

	// When checkRequiredFields is called for the purpose of checking the info form
	// and moving to the shipping page, it is unnecessary to validate the additional
	// fields or the account info.
	const shouldCheckAdditionalFieldsOrAccountInfo = Environment.modalUI.requestedPage() !== 'shipping';
	if (!shouldCheckAdditionalFieldsOrAccountInfo) {
		return true;
	}

	const additionalFieldsValidity = $qs<HTMLFormElement>('#additional-fields-new')?.checkValidity();

	if (!additionalFieldsValidity) {
		// If the user attempted to skip the shipping page despite the required additional
		// fields being incomplete, they will be taken to the shipping page and then shown the
		// incomplete fields.
		if (Environment.modalUI.requestedPage() === 'payment' && Environment.modalUI.page() !== 'shipping') {
			store.dispatch(updateEnvironment({
				modalPageType: 'shipping',
				requestedPage: undefined,
			}));
		}

		let additionalFieldsCount = 0;

		// Selects and expands only the invalid fields.
		$qsAll('.extra-field:invalid').forEach($el => {
			additionalFieldsCount++;
			// The inner div of an accordion row
			const accordionInner
				= (($el.inputMode === 'checkbox') ? $el.parentNode?.parentNode?.parentNode : ['INPUT', 'TEXTAREA'].includes($el.tagName) ? $el.parentNode?.parentNode : $el.parentNode) as HTMLElement;
			if (accordionInner) {
				// Styles the accordion header for the expanded state.
				const accordionHeader = accordionInner.parentNode?.firstElementChild as HTMLElement;
				if (accordionHeader) {
					accordionHeader.classList.add('fill');
					const $chevron = accordionHeader.getElementsByTagName('img')?.[0];
					if ($chevron) {
						$chevron.style.transform = 'scaleY(-1)';
					}
				}

				// Expands the accordion header.
				accordionInner.classList.remove('collapse');
				accordionInner.classList.remove('dim');
			}
		});

		// Waits for all the invalid accordions to open before showing message.
		setTimeout(() => {
			$qs<HTMLFormElement>('#additional-fields-new')?.reportValidity();
		}, (additionalFieldsCount > 1) ? 600 : 0);

		return false;
	}

	// Only check account info when the pay button is clicked from either new or
	// returning checkout.
	const shouldCheckAccountInfo = Environment.modalUI.requestedPage() === undefined;

	// When checkRequiredFields is called to get to the payment page and the additional
	// fields are validated, it is unnecessary to validate the account info.
	if (!shouldCheckAccountInfo) {
		return true;
	}

	// Return true if the account form is not found (means it wasn't required)
	const accountInfoValidity = $qs<HTMLFormElement>('#pp-store-account-info-form')?.checkValidity() ?? true;

	if (accountInfoValidity) {
		return true;
	}

	$qs<HTMLFormElement>('#pp-store-account-info-form')?.reportValidity();
	return false;
}
